import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AtomicButton from '../../atomic/button/Button';
import HOME_TOPIC_ONE from '../../utils/assets/png/BusinessChef2.png';

// Shown behind the image and used as the fallback if the image fails
const FALLBACK_BG = 'linear-gradient(135deg, rgb(8, 54, 67) 0%, rgb(20, 90, 100) 100%)';
const LOAD_TIMEOUT_MS = 8000; // never leave the user stuck on the loader

// Reactive breakpoint (updates on resize / rotation)
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

// Preloads + decodes the image so it paints in one go (no black/blank flash)
// status: 'loading' | 'loaded' | 'error'
const useImageStatus = (src) => {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!src) {
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.src = src;

    const done = (next) => {
      if (!cancelled) setStatus(next);
    };

    const timer = setTimeout(() => done('error'), LOAD_TIMEOUT_MS);

    const onLoaded = () => {
      clearTimeout(timer);
      // decode() makes sure the bitmap is ready before we show it
      if (img.decode) {
        img
          .decode()
          .then(() => done('loaded'))
          .catch(() => done('loaded'));
      } else {
        done('loaded');
      }
    };

    if (img.complete && img.naturalWidth > 0) {
      onLoaded(); // already cached
    } else {
      img.onload = onLoaded;
      img.onerror = () => {
        clearTimeout(timer);
        done('error');
      };
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return status;
};

const Loader = ({ isMobile }) => (
  <div
    style={{
      width: '100%',
      minHeight: isMobile ? '560px' : '1000px',
      background: FALLBACK_BG,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <style>{`@keyframes htoSpin { to { transform: rotate(360deg); } }`}</style>
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '4px solid rgba(255,255,255,0.25)',
        borderTopColor: 'rgb(184, 236, 215)',
        animation: 'htoSpin 0.8s linear infinite',
      }}
    />
  </div>
);

const HomeTopicOne = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const status = useImageStatus(HOME_TOPIC_ONE);

  if (status === 'loading') return <Loader isMobile={isMobile} />;

  const overlay = 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45))';
  let backgroundImage;
  if (status === 'error') {
    backgroundImage = FALLBACK_BG;
  } else {
    backgroundImage = isMobile ? `${overlay}, url(${HOME_TOPIC_ONE})` : `url(${HOME_TOPIC_ONE})`;
  }

  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: 'rgb(8, 54, 67)', // safety net under the image
        backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        minHeight: isMobile ? '560px' : '1000px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isMobile ? 'center' : 'flex-start',
        padding: isMobile ? '48px 20px' : '100px 0 0 150px',
      }}
    >
      <div
        style={{
          color: 'white',
          fontSize: isMobile ? '30px' : '45px',
          lineHeight: isMobile ? '36px' : '54px',
          maxWidth: '600px',
          width: '100%',
          fontFamily: 'Inter Bold',
        }}
      >
        Join a platform built to help restaurants thrive
      </div>

      <div
        style={{
          color: 'white',
          fontSize: isMobile ? '18px' : '30px',
          lineHeight: isMobile ? '26px' : '35px',
          maxWidth: '520px',
          width: '100%',
          fontFamily: 'Inter Medium',
          textAlign: 'start',
          marginTop: isMobile ? '16px' : '20px',
          boxSizing: 'border-box',
        }}
      >
        From customer acquisition to repeat orders, we help restaurants grow faster with smart
        marketing, guaranteed visibility, and zero upfront costs.
      </div>

      <AtomicButton
        label={isMobile ? "Let's Connect" : 'Get started'}
        handleSubmit={() => {
          navigate(isMobile ? '/merchant-contact' : '/checkzipcode');
        }}
        style={{
          width: '100%',
          maxWidth: '325px',
          backgroundColor: 'rgb(184, 236, 215)',
          color: 'rgb(8, 54, 67)',
          marginTop: isMobile ? '28px' : '50px',
        }}
        loading={false}
        disabled={false}
      />
    </div>
  );
};

export default HomeTopicOne;
